import { useState, useRef } from 'react';
import {
  faUpload,
  faSpinner,
  faPaperPlane,
  faTrash,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import Button from 'components/base/Button';
import { errorHandler } from 'helpers/response-utils';
import { Card, Col, Row, Table, Badge, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';

interface Recipient {
  walletAddress: string;
  amount: number;
}

interface AirdropResult {
  walletAddress: string;
  amount: number;
  status: 'success' | 'failed';
  txHash?: string;
  error?: string;
}

const InvestorAirdrop = () => {
  const token = localStorage.getItem('token');
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [results, setResults] = useState<AirdropResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setResults([]);

    const reader = new FileReader();
    reader.onload = event => {
      const text = event.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());

      const parsed: Recipient[] = [];
      const errors: string[] = [];

      lines.forEach((line, index) => {
        if (index === 0) {
          const lower = line.toLowerCase();
          if (lower.includes('wallet') || lower.includes('address')) return;
        }

        const parts = line.split(',').map(p => p.trim().replace(/"/g, ''));
        if (parts.length < 2) {
          errors.push(
            `Row ${index + 1}: expected 2 columns, got ${parts.length}`
          );
          return;
        }

        const walletAddress = parts[0];
        const amount = parseFloat(parts[1]);

        if (
          !walletAddress ||
          walletAddress.length < 32 ||
          walletAddress.length > 44
        ) {
          errors.push(
            `Row ${index + 1}: invalid wallet address "${walletAddress}"`
          );
          return;
        }

        if (isNaN(amount) || amount <= 0) {
          errors.push(`Row ${index + 1}: invalid amount "${parts[1]}"`);
          return;
        }

        parsed.push({ walletAddress, amount });
      });

      if (errors.length > 0) {
        toast.error(`CSV has ${errors.length} error(s): ${errors[0]}`);
      }

      if (parsed.length === 0) {
        toast.error('No valid entries found in CSV');
        return;
      }

      setRecipients(parsed);
      toast.success(`Loaded ${parsed.length} recipient(s) from CSV`);
    };
    reader.readAsText(file);
  };

  const handleClear = () => {
    setRecipients([]);
    setResults([]);
    setFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleExport = () => {
    const headers = [
      'Wallet Address',
      'USDC Amount',
      'Status',
      'Tx Hash',
      'Error'
    ];
    const rows = results.map(r => [
      r.walletAddress,
      r.amount,
      r.status,
      r.txHash || '',
      r.error || ''
    ]);
    const csv = [headers, ...rows]
      .map(row => row.map(val => `"${val}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `airdrop-results-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleAirdrop = async () => {
    if (recipients.length === 0) {
      toast.error('No recipients loaded');
      return;
    }

    setIsLoading(true);
    setResults([]);

    try {
      const preflightRes = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}/investor-airdrop/preflight`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      errorHandler(preflightRes);

      if (preflightRes.data.status !== 'success') {
        toast.error(
          preflightRes.data.error?.message || 'Preflight check failed'
        );
        setIsLoading(false);
        return;
      }

      const { usdcBalance, solBalance, walletAddress } = preflightRes.data.data;

      const batchCount = Math.ceil(recipients.length / 10);
      const solRequired = 0.000005 * batchCount;

      if (solBalance < solRequired) {
        toast.error(
          `Insufficient SOL for transaction fees. Need ~${solRequired.toFixed(6)} SOL (${batchCount} batch(es)), have ${solBalance.toFixed(6)} SOL`
        );
        setIsLoading(false);
        return;
      }

      if (usdcBalance < totalAmount) {
        toast.error(
          `Insufficient USDC balance. Need ${totalAmount.toLocaleString()} USDC, wallet (${walletAddress.slice(0, 8)}...) has ${usdcBalance.toLocaleString()} USDC`
        );
        setIsLoading(false);
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_ENDPOINT}/investor-airdrop`,
        { recipients },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      errorHandler(response);

      if (response.data.status === 'success') {
        setResults(response.data.data.results);
        toast.success(response.data.data.message);
      } else {
        toast.error(response.data.error?.message || 'Airdrop failed');
      }
    } catch (error: any) {
      console.error('Airdrop error:', error);
      toast.error(
        error.response?.data?.error?.message || 'Airdrop request failed'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const totalAmount = recipients.reduce((sum, r) => sum + r.amount, 0);

  return (
    <div>
      <div className="mb-6">
        <Row className="g-3 justify-content-between mb-4">
          <Col xs="auto">
            <h2>Investor Airdrop</h2>
            <p className="text-body-tertiary mb-0">
              Import a CSV with wallet addresses and USDC amounts, then airdrop
              using the active fee payer wallet.
            </p>
          </Col>
        </Row>

        <Card className="mb-4">
          <Card.Body>
            <Row className="g-3 align-items-center">
              <Col xs="auto">
                <input
                  type="file"
                  accept=".csv"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                <Button
                  variant="primary"
                  onClick={() => fileInputRef.current?.click()}
                  startIcon={
                    <FontAwesomeIcon icon={faUpload} className="me-2" />
                  }
                >
                  Import CSV
                </Button>
              </Col>
              {fileName && (
                <Col xs="auto">
                  <span className="text-body-secondary">{fileName}</span>
                </Col>
              )}
              {recipients.length > 0 && (
                <>
                  <Col xs="auto">
                    <Badge bg="info" className="fs-9 px-3 py-2">
                      {recipients.length} recipient(s) &middot;{' '}
                      {totalAmount.toLocaleString()} USDC total
                    </Badge>
                  </Col>
                  <Col xs="auto">
                    <Button
                      variant="success"
                      onClick={handleAirdrop}
                      disabled={isLoading}
                      startIcon={
                        <FontAwesomeIcon
                          icon={isLoading ? faSpinner : faPaperPlane}
                          spin={isLoading}
                          className="me-2"
                        />
                      }
                    >
                      {isLoading ? 'Airdropping...' : 'Airdrop USDC'}
                    </Button>
                  </Col>
                  <Col xs="auto">
                    <Button
                      variant="outline-danger"
                      onClick={handleClear}
                      disabled={isLoading}
                      startIcon={
                        <FontAwesomeIcon icon={faTrash} className="me-2" />
                      }
                    >
                      Clear
                    </Button>
                  </Col>
                </>
              )}
              {results.length > 0 && (
                <Col xs="auto">
                  <Button
                    variant="outline-primary"
                    onClick={handleExport}
                    startIcon={
                      <FontAwesomeIcon icon={faDownload} className="me-2" />
                    }
                  >
                    Export Results
                  </Button>
                </Col>
              )}
            </Row>
          </Card.Body>
        </Card>

        {recipients.length === 0 && results.length === 0 && (
          <Alert variant="secondary">
            <p className="mb-2">
              <strong>CSV Format:</strong> The file should have two columns:
            </p>
            <code>wallet_address, usdc_amount</code>
            <br />
            <br />
            <p className="mb-0 text-body-secondary">
              Example:
              <br />
              <code>7xKXtg2CW87d97TXJSDpbD5jBkheTqA83TZRuJosgAsU, 100</code>
              <br />
              <code>9WzDXwBbmkg8ZTbNMqUxvQRAyrZzDsGYdLVL9zYtAWWM, 250.50</code>
            </p>
          </Alert>
        )}

        {(recipients.length > 0 || results.length > 0) && (
          <Card>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead>
                  <tr>
                    <th className="ps-3">#</th>
                    <th>Wallet Address</th>
                    <th className="text-end">USDC Amount</th>
                    {results.length > 0 && (
                      <th className="text-center">Status</th>
                    )}
                    {results.length > 0 && <th>Tx Hash</th>}
                  </tr>
                </thead>
                <tbody>
                  {(results.length > 0 ? results : recipients).map(
                    (item, index) => {
                      const result = results[index];
                      return (
                        <tr key={index}>
                          <td className="ps-3">{index + 1}</td>
                          <td>
                            <code className="fs-9">{item.walletAddress}</code>
                          </td>
                          <td className="text-end">
                            {Number(item.amount).toLocaleString()}
                          </td>
                          {results.length > 0 && (
                            <td className="text-center">
                              <Badge
                                bg={
                                  result?.status === 'success'
                                    ? 'success'
                                    : 'danger'
                                }
                              >
                                {result?.status}
                              </Badge>
                            </td>
                          )}
                          {results.length > 0 && (
                            <td>
                              {result?.txHash ? (
                                <a
                                  href={`https://solscan.io/tx/${result.txHash}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="fs-9"
                                >
                                  {result.txHash.slice(0, 16)}...
                                </a>
                              ) : (
                                <span className="text-danger fs-9">
                                  {result?.error}
                                </span>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}
      </div>
    </div>
  );
};

export default InvestorAirdrop;
